import { z } from 'zod';
import { getManyFromList } from '@soundxyz/utils';
import { createEnum, enumChecker } from '@soundxyz/utils/const';
import { MAX_CHAR_LENGTH } from '../../validation';

type ErrorKeys =
  | 'required_error'
  | 'total_mismatch'
  | 'pattern_error'
  | 'missing_title'
  | 'missing_cover_image'
  | 'missing_audio'
  | 'missing_reward_field'
  | 'missing_reward_field_title'
  | 'missing_reward_field_description'
  | 'reward_field_description_too_long'
  | 'reward_field_title_too_long';

export const ReleaseInfoErrors = z.object({
  title: z.string().nullish(),
  tokenSymbol: z.string().nullish(),
  tracks: z.string().nullish(),
  coverImage: z.string().nullish(),
  genre: z.string().nullish(),
  behindTheMusic: z.string().nullish(),
  staticCoverImage: z.string().nullish(),
  beatsPerMinute: z.string().nullish(),
  key: z.string().nullish(),
  lyrics: z.string().nullish(),
  license: z.string().nullish(),
  location: z.string().nullish(),
  tempTrack: z.object({
    tempTitle: z.string().nullish(),
    tempCoverImage: z.string().nullish(),
    tempAudio: z.string().nullish(),
  }),
  canCollectorsDownloadAudio: z.string().nullish(),
  curatorRewardsEnabled: z.string().nullish(),
  goldenEggImages: z.string().nullish().array(),
  rewards: z
    .object({
      title: z.string().nullish(),
      description: z.string().max(MAX_CHAR_LENGTH.rewardDescription).nullish(),
    })
    .array(),
});

const ReleaseInfoErrorsOptions = ReleaseInfoErrors.keyof().options;
export type ReleaseInfoErrorKeys = (typeof ReleaseInfoErrorsOptions)[number];

const ReleaseInfoUnnestedErrorsOptions = createEnum(
  getManyFromList(ReleaseInfoErrorsOptions, v =>
    v !== 'rewards' && v !== 'goldenEggImages' && v !== 'tempTrack' ? v : null,
  ),
);
export const isReleaseInfoUnnestedErrorKeysType = enumChecker(ReleaseInfoUnnestedErrorsOptions);

type ReleaseInfoErrors = (typeof ReleaseInfoErrors)['_input'];

export const ErrorMessages: Record<ReleaseInfoErrorKeys, Partial<Record<ErrorKeys, string>>> = {
  title: {
    required_error: 'Title is required',
    pattern_error: 'Title must contain at least one letter or number',
  },
  tokenSymbol: {
    required_error: 'Token symbol is required',
  },
  tracks: {
    required_error: 'Song file is required',
  },
  tempTrack: {
    missing_title: 'Title is required',
    missing_cover_image: 'Cover image is required',
    missing_audio: 'Song file is required',
  },
  coverImage: {
    required_error: 'Cover image is required',
  },
  genre: {
    required_error: 'Genre is required',
  },
  behindTheMusic: {
    required_error: 'Behind the music is required',
  },
  staticCoverImage: {
    required_error: 'Static cover image is required',
  },
  beatsPerMinute: {},
  key: {},
  lyrics: {},
  license: {},
  location: {},
  canCollectorsDownloadAudio: {
    required_error: 'Audio download choice is required',
  },
  curatorRewardsEnabled: {
    required_error: 'Audio download choice is required',
  },
  goldenEggImages: {
    required_error: 'Golden Egg is required',
  },
  rewards: {
    missing_reward_field_title: 'Title is required',
    missing_reward_field_description: 'Description is required',
    reward_field_description_too_long: `Description must be fewer than ${MAX_CHAR_LENGTH.rewardDescription} characters`,
    reward_field_title_too_long: `Title must be fewer than ${MAX_CHAR_LENGTH.rewardTitle} characters`,
  },
};

export const initialReleaseInfoErrors = () =>
  ({
    title: null,
    tokenSymbol: null,
    tracks: null,
    tempTrack: {
      tempTitle: null,
      tempCoverImage: null,
      tempAudio: null,
    },
    coverImage: null,
    genre: null,
    behindTheMusic: null,
    staticCoverImage: null,
    beatsPerMinute: null,
    key: null,
    lyrics: null,
    license: null,
    location: null,
    canCollectorsDownloadAudio: null,
    curatorRewardsEnabled: null,
    goldenEggImages: [],
    rewards: [],
  }) satisfies ReleaseInfoErrors;
